import { useEffect, useRef, useState } from "react";
import { lsClear, lsLoad, lsSave } from "../../tools/Storage";
import StorageEnum from "../../enums/Storage.enum";
import { reloadPage } from "../../tools/Routing";
import { useLoginCheck } from "../../apis/login-check/useLoginCheck.ts";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { toastOptions } from "../../tools/helpers/ToastHelper";
import { getVersion } from "../../tools/ConfigurationReader.ts";
import { useUserProfileSelect } from "../../apis/user-profile/useUserProfileSelect.ts";

export const useLoginController = () => {
  const { t } = useTranslation();
  const usernames = lsLoad(StorageEnum.Usernames);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const loginButtonRef = useRef<HTMLButtonElement | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const { mutate, isPending } = useLoginCheck();
  const { data, isLoading, refetch, isFetchedAfterMount } =
    useUserProfileSelect(false);

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Enter" && loginButtonRef.current) {
      loginButtonRef.current.click();
    }
  };

  const handleChipClick = (username: string) => {
    setUsername(username);
    passwordRef.current?.focus();
  };

  useEffect(() => {
    if (isFetchedAfterMount) {
      if (data) {
        lsSave(StorageEnum.UserProfile, data);
        reloadPage();
      } else {
        lsClear();
        toast.error(t("error.invalidUserProfileResponse"), toastOptions);
      }
    }
  }, [data, isFetchedAfterMount, t]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleLoginClick = () => {
    if (username && password) {
      mutate(
        {
          username,
          password,
        },
        {
          onSuccess: (data) => {
            if (data.token) {
              lsSave(StorageEnum.Jwt, data.token);
              lsSave(StorageEnum.Version, getVersion());

              const usernames = lsLoad(StorageEnum.Usernames);
              if (usernames === null) {
                lsSave(StorageEnum.Usernames, [username]);
              } else {
                if (!usernames.includes(username)) {
                  if (usernames.length === 3) {
                    usernames.shift();
                  }

                  usernames.push(username);
                  lsSave(StorageEnum.Usernames, usernames);
                }
              }

              void refetch();
            } else {
              toast.error(t("error.invalidTokenResponse"), toastOptions);
            }
          },
        },
      );
    }
  };

  return {
    username,
    setUsername,
    password,
    setPassword,
    handleLoginClick,
    loading: isPending || isLoading,
    loginButtonRef,
    usernames,
    handleChipClick,
    passwordRef,
  };
};
